/**
 * Screener wire decode — the framed multi-table payload from qv-data `/ws/scr`.
 *
 * Distinct from {@link decodeArrowTable} on two axes, which is why it lives
 * apart rather than growing options onto the chart decoder:
 *
 *  - **Row-oriented, not columnar.** A grid binds rows. The chart decoder also
 *    collapses each string column to its *first* value as a scalar label, which
 *    is right for `symbol` on a price frame and useless for a screener where
 *    every row carries a different symbol, sector and name.
 *  - **No QV bundle envelope.** No 36-byte LayoutId header and no per-group
 *    status byte; frames are named instead of positional.
 *
 * Wire format (big-endian), mirroring `services/qv-data/app/screener.py`:
 *
 *     [ 4 bytes] num_frames  uint32
 *     per frame:
 *       [ 1 byte ] name_len  uint8
 *       [ N bytes] name      UTF-8  ("detail" | "sector" | "industry")
 *       [ 4 bytes] data_len  uint32
 *       [ N bytes] payload   Arrow IPC stream bytes
 *
 * Screen result schemas are **dynamic** — they differ per screen and change as
 * the upstream engine evolves — so the decoded {@link ScreenerField} list is the
 * authority on what a grid can show. Nothing here knows any column name.
 */

import { Type, tableFromIPC, type Table, type Vector } from "apache-arrow";

/** How a field should be rendered, inferred from its Arrow type. */
export type ScreenerFieldType = "text" | "number" | "date" | "boolean";

/** One column of a screener frame, as discovered from the Arrow schema. */
export interface ScreenerField {
  name: string;
  type: ScreenerFieldType;
}

/** A decoded cell. `Date` for date/timestamp fields; `null` for Arrow nulls. */
export type ScreenerValue = string | number | boolean | Date | null;

/** One decoded row, keyed by field name. */
export type ScreenerRow = Record<string, ScreenerValue>;

/** One named frame: the detail rows, or a server-computed rollup. */
export interface ScreenerFrame {
  /** `"detail"`, `"sector"`, `"industry"`, … — select by name, never position. */
  name: string;
  fields: ScreenerField[];
  rows: ScreenerRow[];
}

/** Name of the per-column metadata frame qv-data sends alongside the data. */
export const COLUMNS_FRAME = "columns";

/**
 * Timeframe a column was derived from, parsed server-side from its name prefix.
 *
 * Units are `s mi h d w m q y`. **`mi` is minute and `m` is month** — not the
 * contract `frequency` spelling (`1m`/`1M`), which separates them by case; a
 * column identifier can't rely on case surviving Polars, Arrow, filter
 * expressions and saved workspaces, and a silent minute/month swap would render
 * wrong data with no error.
 */
export interface ScreenerTimeframe {
  /** Multiples of the unit. An omitted prefix count means 1. */
  count: number;
  unit: "s" | "mi" | "h" | "d" | "w" | "m" | "q" | "y";
}

/** What qv-data knows about one column, beyond its Arrow type. */
export interface ScreenerColumnMeta {
  /** Normalised camelCase id — what `ScreenerConfig.columns` references. */
  field: string;
  /** The upstream engine's original name, so an id is traceable to its source. */
  rawField: string;
  /** The id minus its timeframe prefix, relating a measure across timeframes. */
  baseField: string;
  /** Absent when the column carries the screen's own timeframe. */
  timeframe?: ScreenerTimeframe;
  /** Short header text. Empty until the dictionary lands — fall back to `field`. */
  label: string;
  /** Friendly, searchable name. Empty until the dictionary lands. */
  description: string;
  /**
   * The header band this column falls under by default — `Price`, `Valuation`,
   * `Momentum` (docs/plans/screener-bands.md). Absent: none.
   */
  category?: string;
  /** A native price-row field the live snapshot moves during the session. */
  live?: boolean;
  /**
   * A field for ScanScript, never offered as a column or in a field picker — the
   * chart patterns' 0/1/2 numbers, beside the one `pattern` column. Still in
   * every row and in the compiler's field list.
   */
  scriptOnly?: boolean;
  /**
   * The session this column's value describes — what the header means.
   *
   * ISO date. Absent on a column whose tier has nothing to say, which is not
   * the same as a column with no value.
   */
  date?: string;
  /**
   * When the server evaluated the expression. ISO timestamp, absent if unknown.
   *
   * **Not `asOf`.** That is the name it shipped under on 2026-09-15, before
   * there was a second clock to confuse it with; nothing read it, so it was
   * renamed rather than left ambiguous.
   */
  computeAsOf?: string;
  /**
   * When the *prices* behind the value were captured, which is the realtime
   * writer's fact rather than the compute tier's. ISO timestamp.
   *
   * **The two clocks come apart exactly when it matters.** A writer that
   * stalls leaves a frame in Redis that is present, plausible and hours old; a
   * tick computed over it has a fresh `computeAsOf` and a stale `pricesAsOf`,
   * and only the second says so. On 2026-09-16 that ran for 2h16m of an open
   * session with every other signal reporting healthy.
   *
   * Absent on a settled value — an EOD or base column is a close and was never
   * read from a snapshot, so it has no capture time. Absent is *unknown*, never
   * "fresh": a missing stamp means the producer never said.
   */
  pricesAsOf?: string;
  /** Which rung of the serving ladder answered: `base`, `eod`, `live`, `holdover`. */
  asOfSource?: string;
}

/** Thrown when a payload is truncated or internally inconsistent. */
export class ScreenerDecodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ScreenerDecodeError";
  }
}

function fieldType(typeId: Type): ScreenerFieldType {
  switch (typeId) {
    case Type.Timestamp:
    case Type.Date:
    case Type.Time:
      return "date";
    case Type.Int:
    case Type.Float:
    case Type.Decimal:
      return "number";
    case Type.Bool:
      return "boolean";
    default:
      // Utf8/LargeUtf8/Dictionary and anything unrecognised render as text.
      return "text";
  }
}

/**
 * Normalise one cell. Arrow-JS hands back `Date` for date/timestamp columns and
 * `BigInt` for 64-bit ints; both are converted to shapes a grid can sort and
 * filter without special-casing. Int64 beyond 2^53 loses precision, which is
 * accepted: screener columns are prices, counts and market caps.
 */
function cellValue(raw: unknown, type: ScreenerFieldType): ScreenerValue {
  if (raw == null) return null;

  switch (type) {
    case "date":
      if (raw instanceof Date) return raw;
      // Date32 = days since epoch; Date64/Timestamp = ms (arrow-js normalises).
      {
        const n = Number(raw);
        if (!Number.isFinite(n)) return null;
        return new Date(Math.abs(n) < 1e10 ? n * 86_400_000 : n);
      }
    case "number": {
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }
    case "boolean":
      return Boolean(raw);
    default:
      return typeof raw === "string" ? raw : String(raw);
  }
}

/** Decode one Arrow table into fields + plain row objects. */
export function decodeScreenerTable(input: Uint8Array | Table): {
  fields: ScreenerField[];
  rows: ScreenerRow[];
} {
  const table: Table = input instanceof Uint8Array ? tableFromIPC(input) : input;

  const fields: ScreenerField[] = table.schema.fields.map((f) => ({
    name: f.name,
    type: fieldType(f.typeId),
  }));

  // Read column-wise then transpose: arrow-js row proxies are lazy views over
  // the batches, and a grid needs detached plain objects it can hold and diff.
  const vectors: (Vector | null)[] = fields.map((f) => table.getChild(f.name));
  const rows: ScreenerRow[] = new Array(table.numRows);

  for (let r = 0; r < table.numRows; r++) {
    const row: ScreenerRow = {};
    for (let c = 0; c < fields.length; c++) {
      const field = fields[c]!;
      const vec = vectors[c];
      row[field.name] = vec ? cellValue(vec.get(r), field.type) : null;
    }
    rows[r] = row;
  }

  return { fields, rows };
}

/**
 * Decode a full `/ws/scr` payload into its named frames.
 *
 * @throws {ScreenerDecodeError} if the payload is truncated, a frame length
 * overruns the buffer, or trailing bytes remain — a partial read must fail
 * loudly rather than yield frames that silently lost rows.
 */
export function decodeScreenerFrames(
  input: Uint8Array | ArrayBuffer,
): ScreenerFrame[] {
  const bytes = input instanceof ArrayBuffer ? new Uint8Array(input) : input;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  const need = (offset: number, count: number, what: string): void => {
    if (offset + count > bytes.byteLength) {
      throw new ScreenerDecodeError(
        `truncated payload: need ${count} byte(s) for ${what} at ${offset}, ` +
          `have ${bytes.byteLength - offset}`,
      );
    }
  };

  let offset = 0;
  need(offset, 4, "frame count");
  const frameCount = view.getUint32(offset, false);
  offset += 4;

  const decoder = new TextDecoder();
  const frames: ScreenerFrame[] = [];

  for (let i = 0; i < frameCount; i++) {
    need(offset, 1, `frame ${i} name length`);
    const nameLength = view.getUint8(offset);
    offset += 1;

    need(offset, nameLength, `frame ${i} name`);
    const name = decoder.decode(bytes.subarray(offset, offset + nameLength));
    offset += nameLength;

    need(offset, 4, `frame ${i} ("${name}") payload length`);
    const dataLength = view.getUint32(offset, false);
    offset += 4;

    need(offset, dataLength, `frame ${i} ("${name}") payload`);
    const payload = bytes.subarray(offset, offset + dataLength);
    offset += dataLength;

    frames.push({ name, ...decodeScreenerTable(payload) });
  }

  if (offset !== bytes.byteLength) {
    throw new ScreenerDecodeError(
      `trailing bytes: consumed ${offset} of ${bytes.byteLength}`,
    );
  }

  return frames;
}

/** Look a frame up by name. Frames are named precisely so callers never index. */
export function frameByName(
  frames: readonly ScreenerFrame[],
  name: string,
): ScreenerFrame | undefined {
  return frames.find((f) => f.name === name);
}

const TIMEFRAME_UNITS = new Set(["s", "mi", "h", "d", "w", "m", "q", "y"]);

const text = (row: ScreenerRow, key: string): string => {
  const value = row[key];
  return typeof value === "string" ? value : value == null ? "" : String(value);
};

/**
 * Read the `columns` metadata frame into typed per-column metadata.
 *
 * Returns an empty list when the frame is absent, so a client stays working
 * against a server that predates it — the whole point of naming frames. Rows
 * missing a `field` are skipped rather than producing an unaddressable entry,
 * and an unrecognised timeframe unit is dropped rather than trusted, since a
 * wrong unit would mislabel a column as the wrong period.
 */
export function decodeColumnMeta(
  frames: readonly ScreenerFrame[],
): ScreenerColumnMeta[] {
  const frame = frameByName(frames, COLUMNS_FRAME);
  if (!frame) return [];

  const out: ScreenerColumnMeta[] = [];
  for (const row of frame.rows) {
    const field = text(row, "field");
    if (!field) continue;

    const unit = text(row, "timeframeUnit");
    const count = Number(row["timeframeCount"] ?? 0);
    const meta: ScreenerColumnMeta = {
      field,
      rawField: text(row, "rawField") || field,
      baseField: text(row, "baseField") || field,
      label: text(row, "label"),
      description: text(row, "description"),
    };
    if (unit && TIMEFRAME_UNITS.has(unit) && count > 0) {
      meta.timeframe = { count, unit: unit as ScreenerTimeframe["unit"] };
    }
    // **Copied only when present, never defaulted.** Absent means the server
    // said nothing, and for `pricesAsOf` that is a real and different state
    // from any timestamp — a key whose producer never stamped it is not a
    // fresh key. Writing `""` or `Date.now()` here would erase the distinction
    // the field exists to carry.
    const date = text(row, "date");
    if (date) meta.date = date;
    const computeAsOf = text(row, "computeAsOf");
    if (computeAsOf) meta.computeAsOf = computeAsOf;
    const pricesAsOf = text(row, "pricesAsOf");
    if (pricesAsOf) meta.pricesAsOf = pricesAsOf;
    const asOfSource = text(row, "asOfSource");
    if (asOfSource) meta.asOfSource = asOfSource;
    const category = text(row, "category");
    if (category) meta.category = category;
    if (row["live"] === true) meta.live = true;
    if (row["scriptOnly"] === true) meta.scriptOnly = true;
    out.push(meta);
  }
  return out;
}

/** Render a timeframe as its canonical `[count]unit` string (`w`, `2w`, `15mi`). */
export function formatTimeframe(timeframe: ScreenerTimeframe): string {
  return `${timeframe.count === 1 ? "" : timeframe.count}${timeframe.unit}`;
}
